import { useMemo, type ReactNode } from 'react'
import { Cloud } from 'lucide-react'
import { cn } from '../lib/cn'
import { Button } from '../primitives/Button'
import { SearchInput } from '../primitives/SearchInput'

export interface PromptItem {
  id: string
  name: string
  command?: string
  description?: string
  tags?: string[]
  source?: 'local' | 'server'
}

export interface PromptsViewProps {
  prompts: PromptItem[]
  query: string
  onQuery: (value: string) => void
  activeTag: string | null
  onTag: (tag: string | null) => void
  onUse: (prompt: PromptItem) => void
  onEdit: (prompt: PromptItem) => void
}

export function PromptsView({
  prompts,
  query,
  onQuery,
  activeTag,
  onTag,
  onUse,
  onEdit,
}: PromptsViewProps) {
  const tags = useMemo(() => {
    const set = new Set<string>()
    prompts.forEach((p) => p.tags?.forEach((t) => set.add(t)))
    return [...set]
  }, [prompts])

  const filtered = prompts.filter((p) => {
    const matchesTag = !activeTag || p.tags?.includes(activeTag)
    const q = query.trim().toLowerCase()
    const matchesQuery =
      !q || p.name.toLowerCase().includes(q) || (p.command?.toLowerCase().includes(q) ?? false)
    return matchesTag && matchesQuery
  })

  const local = filtered.filter((p) => p.source !== 'server')
  const server = filtered.filter((p) => p.source === 'server')

  return (
    <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
      <SearchInput value={query} onChange={onQuery} placeholder="Prompts durchsuchen…" />

      <div className="flex flex-wrap gap-2">
        <FilterChip active={activeTag === null} onClick={() => onTag(null)}>
          Alle
        </FilterChip>
        {tags.map((tag) => (
          <FilterChip key={tag} active={activeTag === tag} onClick={() => onTag(tag)}>
            {tag}
          </FilterChip>
        ))}
      </div>

      {local.length > 0 && (
        <Section title="Lokal">
          {local.map((p) => (
            <PromptCard key={p.id} prompt={p} onUse={onUse} onEdit={onEdit} />
          ))}
        </Section>
      )}
      {server.length > 0 && (
        <Section title="Vom Server · firmenweit">
          {server.map((p) => (
            <PromptCard key={p.id} prompt={p} onUse={onUse} onEdit={onEdit} />
          ))}
        </Section>
      )}
    </div>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="space-y-2.5">
      <div className="text-xs font-semibold uppercase tracking-wide text-neutral-400">{title}</div>
      {children}
    </div>
  )
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-full border px-3 py-1 text-sm font-medium transition-colors',
        active
          ? 'border-brand bg-brand text-white'
          : 'border-neutral-200 text-neutral-600 hover:bg-neutral-50',
      )}
    >
      {children}
    </button>
  )
}

function PromptCard({
  prompt,
  onUse,
  onEdit,
}: {
  prompt: PromptItem
  onUse: (p: PromptItem) => void
  onEdit: (p: PromptItem) => void
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onEdit(prompt)}
      onKeyDown={(e) => e.key === 'Enter' && onEdit(prompt)}
      className="cursor-pointer rounded-xl border border-neutral-200 p-3.5 hover:border-neutral-300"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-bold text-neutral-900">{prompt.name}</span>
            {prompt.command && (
              <span className="text-sm font-medium text-accent">{prompt.command}</span>
            )}
          </div>
          {prompt.description && (
            <p className="mt-1 text-sm text-neutral-500">{prompt.description}</p>
          )}
        </div>
        {prompt.source === 'server' && <Cloud className="h-4 w-4 shrink-0 text-brand-500" />}
      </div>
      <div className="mt-3 flex items-end justify-between gap-2">
        <div className="flex flex-wrap gap-1.5">
          {prompt.tags?.map((tag) => (
            <TagPill key={tag} tag={tag} />
          ))}
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={(e) => {
            e.stopPropagation()
            onUse(prompt)
          }}
        >
          Verwenden →
        </Button>
      </div>
    </div>
  )
}

const TAG_COLORS = [
  'bg-purple-50 text-purple-600',
  'bg-green-50 text-green-700',
  'bg-orange-50 text-orange-600',
  'bg-blue-50 text-blue-600',
  'bg-pink-50 text-pink-600',
  'bg-teal-50 text-teal-700',
]

function tagColor(tag: string): string {
  let hash = 0
  for (const ch of tag) hash = (hash + ch.charCodeAt(0)) % TAG_COLORS.length
  return TAG_COLORS[hash] ?? 'bg-neutral-100 text-neutral-600'
}

export function TagPill({ tag }: { tag: string }) {
  return (
    <span className={cn('rounded px-1.5 py-0.5 text-xs font-medium', tagColor(tag))}>{tag}</span>
  )
}
